import React, { useState, useMemo } from "react";
import { useAuth, requireAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { updateUser } from "utilities/db";
import { useFunctions } from "hooks/useFunctions";
import { useToast } from "components/Toast";
import PageHeader from "components/PageLayout";
import UserAvatar from "components/UserAvatar";
import FunctionChip from "components/Functions/FunctionChip";
import { User, Lock, Shield, Briefcase, Mail } from "lucide-react";

function displayNameOf(u = {}) {
  return (
    [u.firstName, u.lastName].filter(Boolean).join(" ") || u.name || u.email || "User"
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <section className="surface rounded-xl ring-1 ring-border overflow-hidden">
      <div className="flex items-center gap-2.5 border-b border-border px-5 py-3.5">
        <span className="grid h-8 w-8 place-items-center rounded-md bg-brand/15 text-brand">
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function ProfilePage() {
  const auth = useAuth();
  const user = auth.user;
  const internal = isInternal(user);
  const { toast } = useToast();

  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [profileSaving, setProfileSaving] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Job functions are internal-only (GET /functions is internal-gated).
  const { functions } = useFunctions({ enabled: internal });
  const fnLabel = useMemo(() => {
    const map = {};
    functions.forEach((f) => (map[f.functionKey] = f.label));
    return (key) => map[key] || key;
  }, [functions]);
  const myFunctions = user.functions || [];

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      await updateUser(user.id, { firstName: firstName.trim(), lastName: lastName.trim() }, user);
      toast({ status: "success", title: "Profile updated" });
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to update profile",
        description: err.response?.data?.error || err.message || "Unknown error",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ status: "warning", title: "Passwords do not match" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ status: "warning", title: "Password must be at least 6 characters" });
      return;
    }
    setPasswordSaving(true);
    try {
      await auth.updatePassword(newPassword);
      toast({ status: "success", title: "Password updated" });
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const msg =
        err.code === "auth/requires-recent-login"
          ? "Please sign out and sign back in, then try again."
          : err.message || "Failed to update password";
      toast({ status: "destructive", title: "Error", description: msg });
    } finally {
      setPasswordSaving(false);
    }
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
      : "Unknown";

  return (
    <div>
      <PageHeader
        title="Profile"
        subtitle={user.email}
        meta={[
          { label: "Role", value: user.role || "—" },
          { label: "Type", value: user.userType || "—" },
        ]}
      />

      <div className="container py-6">
        {/* Identity banner */}
        <div className="surface mb-6 flex items-center gap-4 rounded-xl p-5 ring-1 ring-border">
          <UserAvatar user={user} />
          <div className="min-w-0">
            <div className="truncate text-lg font-semibold text-foreground">{displayNameOf(user)}</div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Mail className="h-3.5 w-3.5" />
              <span className="truncate">{user.email}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {/* Profile info */}
            <Card icon={User} title="Profile Information">
              <form onSubmit={handleProfileSave} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">First name</span>
                    <input
                      type="text"
                      className="input"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </label>
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">Last name</span>
                    <input
                      type="text"
                      className="input"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </label>
                </div>
                <label className="flex flex-col text-sm">
                  <span className="mb-1 font-medium text-foreground">Email</span>
                  <input type="email" className="input opacity-70" value={user.email || ""} disabled />
                  <span className="mt-1 text-xs text-muted-foreground">
                    Email is your sign-in identity and can't be changed here.
                  </span>
                </label>
                <div className="pt-1">
                  <button type="submit" disabled={profileSaving} className="btn btn-primary">
                    {profileSaving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </form>
            </Card>

            {/* Password */}
            <Card icon={Lock} title="Change Password">
              <form onSubmit={handlePasswordSave} className="space-y-4">
                <label className="flex flex-col text-sm">
                  <span className="mb-1 font-medium text-foreground">New password</span>
                  <input
                    type="password"
                    className="input"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="mb-1 font-medium text-foreground">Confirm password</span>
                  <input
                    type="password"
                    className="input"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </label>
                <div className="pt-1">
                  <button type="submit" disabled={passwordSaving} className="btn btn-primary">
                    {passwordSaving ? "Saving…" : "Update password"}
                  </button>
                </div>
              </form>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card icon={Shield} title="Account">
              <dl className="space-y-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground">Role</dt>
                  <dd>
                    <span className="badge badge-muted capitalize">{user.role || "—"}</span>
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground">User type</dt>
                  <dd>
                    <span className="badge badge-muted capitalize">{user.userType || "—"}</span>
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground">Member since</dt>
                  <dd className="text-foreground">{formatDate(user.createdAt)}</dd>
                </div>
              </dl>
            </Card>

            {internal && (
              <Card icon={Briefcase} title="Job Functions">
                {myFunctions.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {myFunctions.map((key) => (
                      <FunctionChip key={key} label={fnLabel(key)} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No functions assigned yet.</p>
                )}
                <p className="mt-3 text-xs text-muted-foreground">
                  Functions are assigned by your team admin on the Team page.
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default requireAuth(ProfilePage);
